import { useCallback, useEffect, useRef, useState } from 'react';
import { routes, tabOf, type Tab } from './routes';

/**
 * Навигация приложения поверх истории браузера.
 *
 * Своя, а не библиотека, ровно по одной причине: в установленной PWA у пользователя
 * нет кнопки «Назад» — есть только наш жест от левого края и наша стрелка в шапке.
 * Значит, переход, его направление и анимация должны быть одним состоянием, которым
 * мы управляем. Библиотека маршрутизации решает другую задачу — адреса, — и отдаёт
 * направление перехода последней.
 *
 * Адрес при этом настоящий: `history.pushState`, рабочая кнопка «назад» в браузере
 * и ссылка, которую можно открыть напрямую.
 */

export type Direction = 'push' | 'pop' | 'none';

interface HistoryState {
  index: number;
}

export interface Navigator {
  path: string;
  direction: Direction;
  /** Есть ли куда возвращаться: по этому шапка решает, рисовать ли «Назад». */
  canGoBack: boolean;
  push: (path: string) => void;
  replace: (path: string) => void;
  back: () => void;
  /** Переключение раздела: не накапливает историю, как и на Android. */
  switchTab: (tab: Tab) => void;
  tab: Tab;
}

const indexOf = (state: unknown): number =>
  typeof state === 'object' && state !== null && 'index' in state
    ? Number((state as HistoryState).index)
    : 0;

export function useNavigator(): Navigator {
  const [path, setPath] = useState(() =>
    window.location.pathname === '/' ? routes.cases : window.location.pathname,
  );
  const [direction, setDirection] = useState<Direction>('none');
  const index = useRef(indexOf(window.history.state));

  useEffect(() => {
    /*
     * Корень приводится к списку дел, а первый вход получает номер.
     *
     * Номер нужен, чтобы после «назад» было с чем сравнивать и знать направление
     * анимации. Приведение корня — чтобы адрес всегда назывался: иначе «назад»
     * из поиска возвращает на «/», и это тот же список дел под другим именем.
     */
    const path = window.location.pathname === '/' ? routes.cases : window.location.pathname;

    if (!window.history.state || path !== window.location.pathname) {
      window.history.replaceState({ index: 0 } satisfies HistoryState, '', path);
      setPath(path);
    }

    const onPopState = (event: PopStateEvent) => {
      const next = indexOf(event.state);

      setDirection(next < index.current ? 'pop' : 'push');
      index.current = next;
      setPath(window.location.pathname);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const push = useCallback((next: string) => {
    index.current += 1;
    window.history.pushState({ index: index.current } satisfies HistoryState, '', next);

    setDirection('push');
    setPath(next);
  }, []);

  const replace = useCallback((next: string) => {
    window.history.replaceState({ index: index.current } satisfies HistoryState, '', next);

    setDirection('none');
    setPath(next);
  }, []);

  const back = useCallback(() => window.history.back(), []);

  const switchTab = useCallback(
    (tab: Tab) => {
      const next = `/${tab}`;
      if (window.location.pathname === next) return;

      // Раздел заменяет раздел, а не ложится на него: иначе «назад» уводило бы
      // по всем открытым вкладкам подряд, а не из записи в список.
      replace(next);
    },
    [replace],
  );

  return {
    path,
    direction,
    canGoBack: index.current > 0,
    push,
    replace,
    back,
    switchTab,
    tab: tabOf(path),
  };
}
